define([
    'lodash',
    'reactDOM',
    'gridComponent/core/grid',
    'gridComponent/core/enums',
    'gridComponent/core/measure',
    'skins',
    'gridComponent/skins/skins.json',
    'santa-components',
    'componentsCore'
], function (
    _,
    ReactDOM,
    Grid,
    enums,
    measure,
    skinsPackage,
    skinsJson,
    santaComponents,
    componentsCore
) {
    'use strict';

    const THEME_CLASS_NAME = 'ag-fresh';

    const transformPropsWithClonedData = props =>
        _.assign({}, props, {compData: _.cloneDeep(props.compData)});

    const grid = {
        displayName: 'Grid',
        mixins: [componentsCore.mixins.skinBasedComp, santaComponents.mixins.runTimeCompData],

        propTypes: {
            compData: santaComponents.santaTypesDefinitions.Component.compData.isRequired,
            compProp: santaComponents.santaTypesDefinitions.Component.compProp.isRequired,
            compTheme: santaComponents.santaTypesDefinitions.Component.theme.isRequired,
            linkRenderInfo: santaComponents.santaTypesDefinitions.Link.renderInfo,
            rootNavigationInfo: santaComponents.santaTypesDefinitions.Component.rootNavigationInfo.isRequired,
            isViewerMode: santaComponents.santaTypesDefinitions.isViewerMode,
            getMediaFullStaticUrl: santaComponents.santaTypesDefinitions.ServiceTopology.getMediaFullStaticUrl,
            componentViewMode: santaComponents.santaTypesDefinitions.RenderFlags.componentViewMode,
            registerLayoutFunc: santaComponents.santaTypesDefinitions.Layout.registerLayoutFunc.isRequired,
            isExperimentOpen: santaComponents.santaTypesDefinitions.isExperimentOpen
        },

        statics: {
            behaviors: {
                modifyRow: {methodName: 'modifyRow', params: ['rowDataDelta']},
                modifySelection: {methodName: 'modifySelection', params: ['userSelection']}
            }
        },

        modifyRow(delta) {
            if (this.grid) {
                Grid.modifyRow(this.grid, this.props, delta);
            }
        },

        modifySelection(userSelection) {
            if (this.grid) {
                Grid.modifySelection(this.grid, this.props, userSelection);
            }
        },

        getClasses(props) {
            const classes = {};
            classes[THEME_CLASS_NAME] = true;
            // This scenario covers having 0 rows set from SDK.dataFetcher or SDK.rows.
            // Should still hide the pagination buttons in case the grid component is filtering static data.
            classes['hide-pagination-panel'] = props.compData.rows.length === 0;
            classes['align-text-left'] = props.compProp.horizontalAlignment === enums.HorizontalAlignment.LEFT;
            classes['align-text-right'] = props.compProp.horizontalAlignment === enums.HorizontalAlignment.RIGHT;
            classes['align-text-center'] = props.compProp.horizontalAlignment === enums.HorizontalAlignment.CENTER;
            classes['disable-horizontal-scroll'] = props.compProp.columnLayout === enums.ColumnLayout.EQUAL;
            classes['select-rows'] = props.compProp.userSelectionType === enums.UserSelectionType.ROW;
            classes['select-cells'] = props.compProp.userSelectionType === enums.UserSelectionType.CELL;
            classes['hide-header'] = !props.compProp.showHeader;
            return classes;
        },

        componentDidMount() {
            this.grid = Grid(this); // eslint-disable-line new-cap

            // This 'isFunction' is needed for live site:
            this.transformedProps = _.isFunction(this.transformPropsWithPreviewDummyData) ?
                this.transformPropsWithPreviewDummyData(this.props) : transformPropsWithClonedData(this.props);

            Grid.init(this.grid, this.transformedProps, this.refs.grid);
            this.props.registerLayoutFunc(ReactDOM.findDOMNode(this),
                node => measure(node, _.pick(this.props, ['compData', 'compProp', 'compTheme'])));
        },

        componentWillReceiveProps(nextProps) {
            const viewModeChanged = nextProps.componentViewMode !== this.props.componentViewMode;

            // This 'isFunction' is needed for live site:
            const transformedNextProps = _.isFunction(this.transformPropsWithIncompleteData) ?
                this.transformPropsWithIncompleteData(nextProps, viewModeChanged) : nextProps;

            if (viewModeChanged) {
                Grid.handleViewModeChange(this.grid, this.props, transformedNextProps, this.props.componentViewMode);
            }

            Grid.update(this.grid, this.transformedProps, transformedNextProps, this.refs.grid, this.props.componentViewMode);
            this.transformedProps = transformPropsWithClonedData(transformedNextProps);
        },

        getSkinProperties() {
            const classes = this.getClasses(this.transformedProps || this.props);
            const skinProps = {
                '': {
                    className: _.reduce(classes, function (className, value, key) {
                        if (value) {
                            className += ` ${key}`;
                        }

                        return className;
                    }, '')
                }
            };

            return skinProps;
        },

        componentWillUnmount() {
            Grid.destroy(this.grid);
        }
    };

    componentsCore.compRegistrar.register('wysiwyg.viewer.components.Grid', grid);
    skinsPackage.skinsMap.addBatch(skinsJson);

    return grid;
});
